import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageSquareText, X, Clock } from "lucide-react";
import { SITE } from "../config/site";

export function CallTextDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="book-appointment-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md border border-edge bg-white p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crimson">
              Book an Appointment
            </p>
            <h2 className="mt-2 font-display text-3xl font-black uppercase leading-none text-slate-50">
              Call or Text Us
            </h2>
          </div>
          <button
            data-testid="book-dialog-close-button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center border border-edge text-slate-400 transition-colors hover:border-crimson hover:text-crimson"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          The fastest way to get your brakes done — reach out and we'll lock in a day and time that works for you.
        </p>
        <p data-testid="book-dialog-phone" className="mt-5 text-center font-display text-4xl font-black tracking-tight text-crimson">
          {SITE.phone}
        </p>
        <div className="mt-5 grid gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="book-call-button"
            className="flex min-h-[56px] items-center justify-center gap-2 bg-crimson font-display text-xl font-bold uppercase text-white transition-colors hover:bg-crimsondark"
          >
            <Phone className="h-5 w-5" /> Call Now
          </a>
          <a
            href={SITE.smsHref}
            data-testid="book-text-button"
            className="flex min-h-[56px] items-center justify-center gap-2 border-2 border-crimson font-display text-xl font-bold uppercase text-crimson transition-colors hover:bg-crimson hover:text-white"
          >
            <MessageSquareText className="h-5 w-5" /> Text Us
          </a>
        </div>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500">
          <Clock className="h-3.5 w-3.5" /> {SITE.hours} · {SITE.closedNote}
        </p>
      </div>
    </div>
  );
}

export const QuoteButton = ({ className = "", testid = "get-brake-quote-button", to = "/get-a-brake-quote", children }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex min-h-[52px] items-center justify-center gap-2 bg-crimson px-7 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-crimsondark focus:outline-none focus:ring-2 focus:ring-crimson focus:ring-offset-2 focus:ring-offset-obsidian ${className}`}
  >
    {children || "Get a Brake Quote"}
    <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

export const BookButton = ({ className = "", testid = "book-appointment-button" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen(true)}
        className={`group inline-flex min-h-[52px] items-center justify-center gap-2 border-2 border-amber2 px-7 font-display text-lg font-bold uppercase tracking-wide text-amber2 transition-colors duration-200 hover:bg-amber2 hover:text-obsidian focus:outline-none focus:ring-2 focus:ring-amber2 focus:ring-offset-2 focus:ring-offset-obsidian ${className}`}
      >
        <Phone className="h-5 w-5" />
        Book an Appointment
      </button>
      <CallTextDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export const CTAPair = ({ className = "" }) => (
  <div className={`flex flex-col gap-3 sm:flex-row ${className}`}>
    <QuoteButton />
    <BookButton />
  </div>
);
